import { BackgroundCheckConfig } from '@/backend/features/background-check/defaults';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import { useBackgroundCheckStorage } from '@/ui/features/background-check/hooks/useBackgroundCheckStorage';
import { getBackgroundCheckStatusLabel } from '@/ui/utils';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { useClientSideExport } from '@juvare/platform-sdk-react/ui/features/clientside-export/hooks/useClientSideExport';
import { Filter } from '@juvare/platform-shared/features/storage';
import { useTranslation } from 'react-i18next';

interface UseBackgroundCheckExportProps {
    filter?: Filter<BackgroundCheckColumns>[];
    orderBy?: {
        column: keyof BackgroundCheckColumns;
        desc?: boolean;
    }[];
    numberOfActiveBackgroundCheckTypes?: number;
    backgroundCheckConfig?: BackgroundCheckConfig;
}

export function useBackgroundCheckExport({
    filter,
    orderBy,
    numberOfActiveBackgroundCheckTypes,
    backgroundCheckConfig,
}: UseBackgroundCheckExportProps = {}): {
    handleExportBackgroundCheck: () => Promise<void>;
} {
    const { t } = useTranslation('rms');
    const storage = useBackgroundCheckStorage();

    const showTypeColumn = numberOfActiveBackgroundCheckTypes && numberOfActiveBackgroundCheckTypes > 1;

    const { handleExport: handleExportBackgroundCheck } = useClientSideExport({
        header: [
            t('credentialing.credentialVerificationDetails.labels.name'),
            t('accountsFilters.backgroundCheck.status'),
            ...(showTypeColumn ? [t('accountsFilters.backgroundCheck.type')] : []),
            t('backgroundCheck.labels.lastUpdated'),
        ],
        fileName: 'background-check',
        onFetchData: ({ offset, limit }) =>
            storage.list({
                offset,
                limit,
                filter: filter ?? [],
                orderBy: orderBy ?? [{ column: 'accountDisplayName', desc: false }],
            }),
        onConvertData: (data) => {
            const status = getBackgroundCheckStatusLabel({ status: data.columns.status, t });
            const updatedAt = data.columns.updatedAt ? formatDateTime(new Date(data.columns.updatedAt)) : '';
            const type = backgroundCheckConfig?.type?.[data.columns.type as BackgroundCheckType]?.label ?? '';

            return [data.columns.accountDisplayName ?? '', ...(showTypeColumn ? [type] : []), status, updatedAt];
        },
    });

    return { handleExportBackgroundCheck };
}
